import { useCallback, useMemo, useState } from "react";
import { DraftAPI } from "../components/api";

function getErrorMessage(err) {
  if (
    err &&
    err.response &&
    err.response.data &&
    typeof err.response.data.message === "string"
  ) {
    return err.response.data.message;
  }
  if (err && typeof err.message === "string") return err.message;
  return "Something went wrong";
}

export default function useDraft(formType, section) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDraft(null);
    try {
      const res = await DraftAPI.getLatestDraft({ formType, section });
      if (res && res.formStatus && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("formStatusChanged", {
            detail: { formType, status: res.formStatus },
          }),
        );
      }
      if (
        res &&
        typeof res === "object" &&
        res.draftKey &&
        (res.draftKey.formType !== formType || res.draftKey.section !== section)
      ) {
        setDraft(null);
        return null;
      }
      const loaded = res && typeof res === "object" && "data" in res ? res.data : null;
      setDraft(loaded || null);
      return loaded || null;
    } catch (err) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [formType, section]);

  const saveDraft = useCallback(
    async (data) => {
      setError(null);

      setSaving(true);
      try {
        const res = await DraftAPI.saveDraft({ formType, section, data });
        if (
          res &&
          typeof res === "object" &&
          res.draftKey &&
          (res.draftKey.formType !== formType || res.draftKey.section !== section)
        ) {
          setDraft(null);
          return null;
        }
        if (res && typeof res === "object" && res.skipped) {
          return { skipped: true, reason: res.reason };
        }
        const saved = res && typeof res === "object" && "data" in res ? res.data : null;
        setDraft(saved || null);
        if (saved && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("formStatusChanged", {
              detail: { formType, status: res.formStatus || "draft" },
            }),
          );
        }
        return saved || null;
      } catch (err) {
        setError(getErrorMessage(err));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [formType, section],
  );

  const deleteDraft = useCallback(async () => {
    setError(null);
    try {
      const res = await DraftAPI.deleteDraft({ formType, section });
      setDraft(null);
      if (res && res.formStatus && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("formStatusChanged", {
            detail: { formType, status: res.formStatus },
          }),
        );
      }
      return true;
    } catch (err) {
      setError(getErrorMessage(err));
      return false;
    }
  }, [formType, section]);

  return useMemo(
    () => ({
      draft,
      loading,
      saving,
      error,
      loadDraft,
      saveDraft,
      deleteDraft,
      lastDraftedAt:
        draft && draft.savedAt
          ? new Date(draft.savedAt)
          : null,
    }),
    [draft, loading, saving, error, loadDraft, saveDraft, deleteDraft],
  );
}
